import React, { useEffect, useState, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography, MenuItem, Select, FormControl, InputLabel,
  CircularProgress, Chip,
} from '@material-ui/core';
import ActionCard from './ActionCard';
import { SERVER_NAME } from './config';

const API_BASE = SERVER_NAME;

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '32px 20px 60px',
    color: '#e5e7ff',
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: 900,
    letterSpacing: -0.5,
    color: '#e5e7ff',
    marginBottom: 4,
  },
  pageSubtitle: {
    fontSize: 13,
    color: '#9ea4d8',
    marginBottom: 24,
  },
  summaryRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 24,
  },
  statCard: {
    background: 'rgba(10, 14, 30, 0.92)',
    border: '1px solid rgba(77,121,255,0.22)',
    borderRadius: 12,
    padding: '12px 18px',
    minWidth: 110,
  },
  statLabel: {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: '#9ea4d8',
    marginBottom: 4,
  },
  statValue: {
    fontSize: 22,
    fontWeight: 900,
    color: '#e5e7ff',
  },
  filtersRow: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: 20,
  },
  select: {
    minWidth: 140,
    '& .MuiInputBase-root': {
      color: '#e5e7ff',
      fontSize: 13,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(77,121,255,0.35)',
    },
    '& .MuiInputLabel-outlined': {
      color: '#9ea4d8',
    },
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: '#e5e7ff',
    marginBottom: 12,
    marginTop: 24,
  },
  empty: {
    fontSize: 13,
    color: '#9ea4d8',
    marginTop: 20,
    textAlign: 'center',
    padding: '40px 0',
  },
  link: {
    color: '#9ef7c5',
    textDecoration: 'none',
    fontWeight: 900,
    '&:hover': { textDecoration: 'underline' },
  },
}));

function SummaryCards({ summary }) {
  const classes = useStyles();
  if (!summary) return null;
  const open = (summary.breakdown || []).filter((r) => r.status === 'open').reduce((s, r) => s + Number(r.count), 0);
  const confirmed = (summary.breakdown || []).filter((r) => r.status === 'confirmed').reduce((s, r) => s + Number(r.count), 0);
  const failed = (summary.breakdown || []).filter((r) => r.status === 'failed').reduce((s, r) => s + Number(r.count), 0);

  return (
    <div className={classes.summaryRow}>
      <div className={classes.statCard}>
        <div className={classes.statLabel}>Total (7d)</div>
        <div className={classes.statValue}>{summary.total_7d ?? '—'}</div>
      </div>
      <div className={classes.statCard}>
        <div className={classes.statLabel}>Open</div>
        <div className={classes.statValue} style={{ color: '#9ef7c5' }}>{open}</div>
      </div>
      <div className={classes.statCard}>
        <div className={classes.statLabel}>Confirmed</div>
        <div className={classes.statValue} style={{ color: '#9ef7c5' }}>{confirmed}</div>
      </div>
      <div className={classes.statCard}>
        <div className={classes.statLabel}>Failed</div>
        <div className={classes.statValue} style={{ color: '#ff4d6a' }}>{failed}</div>
      </div>
    </div>
  );
}

export default function ActionsPage() {
  const classes = useStyles();

  const [summary, setSummary] = useState(null);
  const [actions, setActions] = useState([]);
  const [outcomes, setOutcomes] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [directionFilter, setDirectionFilter] = useState('');
  const [actionTypeFilter, setActionTypeFilter] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/actions/summary`)
      .then((r) => r.json())
      .then(setSummary)
      .catch(() => {});
  }, []);

  const loadActions = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 50 });
    if (statusFilter)     params.set('status', statusFilter);
    if (directionFilter)  params.set('direction', directionFilter);
    if (actionTypeFilter) params.set('action_type', actionTypeFilter);

    fetch(`${API_BASE}/actions?${params}`)
      .then((r) => r.json())
      .then((data) => {
        setActions(data.actions || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [statusFilter, directionFilter, actionTypeFilter]);

  useEffect(() => { loadActions(); }, [loadActions]);

  // Fetch outcomes for non-open actions
  useEffect(() => {
    const closed = actions.filter((a) => !['open', 'evaluating'].includes(a.status));
    if (!closed.length) return;

    const fetches = closed.map((a) =>
      fetch(`${API_BASE}/actions/${a.action_id}/outcome`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => data ? [String(a.action_id), data.outcome] : null)
        .catch(() => null)
    );
    Promise.all(fetches).then((results) => {
      const map = {};
      for (const r of results) { if (r) map[r[0]] = r[1]; }
      setOutcomes((prev) => ({ ...prev, ...map }));
    });
  }, [actions]);

  return (
    <div className={classes.root}>
      <Typography className={classes.pageTitle}>Decision Layer Actions</Typography>
      <Typography className={classes.pageSubtitle}>
        Rule-based signals derived from LLM-generated theses via the{' '}
        <a href="/methodology" className={classes.link}>OTA methodology</a>.
        Each action scores thesis conviction, risk pressure, and overlap convergence against calibrated thresholds — no discretionary judgment, no LLM in the loop.
      </Typography>

      <SummaryCards summary={summary} />

      <div className={classes.filtersRow}>
        <FormControl variant="outlined" size="small" className={classes.select}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
            <MenuItem value="">All</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="confirmed">Confirmed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="evaluating">Evaluating</MenuItem>
            <MenuItem value="inconclusive">Inconclusive</MenuItem>
            <MenuItem value="invalidated">Invalidated</MenuItem>
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" className={classes.select}>
          <InputLabel>Direction</InputLabel>
          <Select value={directionFilter} onChange={(e) => setDirectionFilter(e.target.value)} label="Direction">
            <MenuItem value="">All</MenuItem>
            <MenuItem value="bullish">Bullish</MenuItem>
            <MenuItem value="bearish">Bearish</MenuItem>
            <MenuItem value="neutral">Neutral</MenuItem>
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" className={classes.select}>
          <InputLabel>Type</InputLabel>
          <Select value={actionTypeFilter} onChange={(e) => setActionTypeFilter(e.target.value)} label="Type">
            <MenuItem value="">All</MenuItem>
            <MenuItem value="paper_buy">Paper Buy</MenuItem>
            <MenuItem value="accumulate_signal">Accumulate</MenuItem>
            <MenuItem value="watch">Watch</MenuItem>
            <MenuItem value="reduce_exposure_signal">Reduce Exposure</MenuItem>
            <MenuItem value="avoid">Avoid</MenuItem>
            <MenuItem value="confirm_risk">Confirm Risk</MenuItem>
            <MenuItem value="investigate">Investigate</MenuItem>
            <MenuItem value="request_more_evidence">More Evidence</MenuItem>
          </Select>
        </FormControl>

        {(statusFilter || directionFilter || actionTypeFilter) ? (
          <Chip
            size="small"
            label="Clear filters"
            onClick={() => { setStatusFilter(''); setDirectionFilter(''); setActionTypeFilter(''); }}
            style={{ background: 'rgba(77,121,255,0.15)', color: '#cfd8ff', border: '1px solid rgba(77,121,255,0.35)', cursor: 'pointer' }}
          />
        ) : null}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
          <CircularProgress size={28} style={{ color: '#4d79ff' }} />
        </div>
      ) : !actions.length ? (
        <Typography className={classes.empty}>No actions found with current filters.</Typography>
      ) : (
        <>
          <Typography className={classes.sectionTitle}>{`${actions.length} action${actions.length !== 1 ? 's' : ''}`}</Typography>
          {actions.map((action) => (
            <ActionCard
              key={action.action_id}
              action={action}
              outcome={outcomes[String(action.action_id)] || null}
            />
          ))}
        </>
      )}
    </div>
  );
}
